import type { ComponentType } from 'react'
import { ArrowRight, Calendar } from 'lucide-react'
import { SiteLayout } from '../components/SiteLayout'
import { formatDate, type ContentEntry } from '../lib/content'
import { authorForByline, authorSlugPath } from '../lib/authors'

/**
 * Shared long-form detail page for blog posts, changelog entries, and customer
 * stories. Header (eyebrow, title, intro, author, cover) sits on the reading
 * measure; the MDX body renders inside `container-prose prose`. The body is a
 * code-split chunk passed in already resolved (see content-routes / the preload
 * in entry-server / entry-client).
 */
export function ArticlePage({
  entry,
  Content,
  backHref,
  backLabel,
  beforeContent,
  afterContent,
}: {
  entry: ContentEntry
  Content?: ComponentType
  backHref: string
  backLabel: string
  /** Optional extra section rendered after the cover image, before the MDX
   * body — for a post that needs something the clean-markdown MDX body
   * can't hold (the .mdx must stay warning-free for the AI-crawler markdown
   * export, see tests/blog-markdown.test.ts). Wired per-slug in
   * content-routes.tsx; every other post leaves this unset. */
  beforeContent?: ComponentType
  /** Same idea as `beforeContent`, rendered after the MDX body instead of
   * before it, for interactive material that recaps or explores a story a
   * reader needs to have already read to make sense of (jargon-heavy tabs,
   * a turn-by-turn stepper). Wired per-slug in content-routes.tsx; every
   * other post leaves this unset. */
  afterContent?: ComponentType
}) {
  const { meta } = entry
  // Link the byline to the author's bio page when the free-text byline resolves
  // to a known author (blog only; changelog/customer stories have no author page).
  const author = entry.collection === 'blog' ? authorForByline(meta.author) : undefined
  const authorHref = author ? authorSlugPath(author) : undefined
  const tag = entry.collection === 'changelog' ? meta.version : meta.category
  const hasByline = Boolean(meta.author || meta.date || tag)

  return (
    <SiteLayout>
      <article className="pt-28 pb-20 md:pt-32 md:pb-28">
        <div className="container-prose">
          <a
            href={backHref}
            className="text-[13px] font-medium text-ink-500 transition-colors hover:text-ink-900"
          >
            ← {backLabel}
          </a>
          <h1 className="display mt-5 text-[30px] leading-[1.1] text-ink-900 md:text-[42px]">
            {meta.title}
          </h1>
          {meta.intro && (
            <p className="mt-5 text-[18px] leading-[1.6] text-ink-600 md:text-[19px]">
              {meta.intro}
            </p>
          )}
          {hasByline && (
            <div className="mt-7 flex items-center gap-3 text-[14px] text-ink-500">
              {meta.authorPhoto &&
                (authorHref ? (
                  <a href={authorHref} aria-label={meta.author}>
                    <img
                      src={meta.authorPhoto}
                      alt={meta.author ?? ''}
                      className="h-9 w-9 rounded-full object-cover"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <img
                    src={meta.authorPhoto}
                    alt={meta.author ?? ''}
                    className="h-9 w-9 rounded-full object-cover"
                    loading="lazy"
                  />
                ))}
              <span>
                {meta.author &&
                  (authorHref ? (
                    <a
                      href={authorHref}
                      className="font-medium text-ink-600 transition-colors hover:text-ink-900"
                    >
                      {meta.author}
                    </a>
                  ) : (
                    meta.author
                  ))}
                {meta.date && (
                  <>
                    {meta.author && '  ·  '}
                    <time dateTime={meta.date}>{formatDate(meta.date)}</time>
                  </>
                )}
                {meta.updated && (
                  <>
                    {'  ·  Updated '}
                    <time dateTime={meta.updated}>{formatDate(meta.updated)}</time>
                  </>
                )}
                {tag && `  ·  ${tag}`}
              </span>
            </div>
          )}
        </div>

        {meta.image && (
          <div className="container-page mt-10 md:mt-12">
            <img
              src={meta.image}
              alt={meta.title}
              className="aspect-[3/2] w-full rounded-2xl border border-ink-100 object-cover object-[center_30%]"
              loading="lazy"
            />
          </div>
        )}

        {beforeContent && (
          <div className="container-prose mt-10 md:mt-12">
            {(() => {
              const Before = beforeContent
              return <Before />
            })()}
          </div>
        )}

        <div className="container-prose prose mt-10 md:mt-12">
          {Content ? <Content /> : null}
        </div>

        {afterContent && (
          <div className="container-prose mt-10 md:mt-12">
            {(() => {
              const After = afterContent
              return <After />
            })()}
          </div>
        )}

        {entry.collection === 'blog' && (
          <div className="container-prose mt-14 md:mt-16">
            <div className="rounded-2xl border border-ink-100 bg-paper-soft/60 p-6 md:p-7">
              <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ink-400">
                Get started
              </div>
              <h2 className="mt-3 text-[24px] font-semibold leading-[1.15] text-ink-900 md:text-[28px]">
                Put this into production with LangWatch.
              </h2>
              <p className="mt-3 text-[15px] leading-[1.6] text-ink-500">
                Trace your agents, run evaluations, and turn failures into repeatable tests.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="/get-a-demo"
                  className="inline-flex items-center gap-2 rounded-full bg-ink-900 px-5 py-3 text-[13.5px] font-semibold text-paper transition hover:bg-ink-700"
                >
                  <Calendar className="h-4 w-4" />
                  Talk to an engineer
                </a>
                <a
                  href="https://app.langwatch.ai/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-paper px-5 py-3 text-[13.5px] font-semibold text-ink-900 transition hover:border-ink-300"
                >
                  Start free
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {entry.collection === 'blog' && author?.bio && (
          <aside aria-label="About the author" className="container-prose mt-10 md:mt-12">
            <div className="flex items-start gap-4 rounded-2xl border border-ink-100 p-6">
              {author.photo && (
                <img
                  src={author.photo}
                  alt={author.name}
                  className="h-12 w-12 shrink-0 rounded-full object-cover"
                  loading="lazy"
                />
              )}
              <div>
                <div className="text-[15px] font-semibold text-ink-900">
                  {authorHref ? (
                    <a href={authorHref} className="transition-colors hover:text-ink-600">
                      {author.name}
                    </a>
                  ) : (
                    author.name
                  )}
                </div>
                {author.role && <div className="text-[13px] text-ink-500">{author.role}</div>}
                <p className="mt-2 text-[14px] leading-[1.6] text-ink-600">{author.bio}</p>
              </div>
            </div>
          </aside>
        )}

        {meta.faq && meta.faq.length > 0 && (
          <div className="container-prose mt-14 md:mt-16">
            <div className="border-t border-ink-100 pt-10">
              <h2 className="display text-[24px] leading-[1.15] text-ink-900 md:text-[30px]">
                Frequently asked questions
              </h2>
              <dl className="mt-7 divide-y divide-ink-100">
                {meta.faq.map((f) => (
                  <div key={f.q} className="py-5">
                    <dt className="text-[17px] font-semibold text-ink-900 md:text-[18px]">{f.q}</dt>
                    <dd className="mt-2 text-[16px] leading-[1.6] text-ink-600 md:text-[17px]">
                      {f.a}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}
      </article>
    </SiteLayout>
  )
}
